import { APIClient } from "../helpers/api_helper";
import * as url from "../helpers/url_helper";
import {
  JobItem,
  JobCreateRequest,
  JobUpdateRequest,
  JobsResponse,
} from "../interfaces/Job";
import { DEFAULT_PAGE_SIZE } from "../common/constants";

const api = new APIClient();

/**
 * Get all jobs with optional filters (paginated)
 * Uses /api/v1/Job endpoint (formerly Project)
 * @param pageNumber - Page number (default: 1)
 * @param pageSize - Page size (default: DEFAULT_PAGE_SIZE)
 * @param clientId - Optional client filter (UUID)
 * @param status - Optional status filter (integer)
 * @param search - Optional search term (searches JobNumber and Title)
 * @returns Promise with paginated jobs response
 */
export const getProjects = (
  pageNumber: number = 1,
  pageSize: number = DEFAULT_PAGE_SIZE,
  clientId?: string,
  status?: number,
  search?: string
): Promise<JobsResponse> => {
  const params: Record<string, string | number> = {
    pageNumber,
    pageSize,
  };
  
  if (clientId) params.clientId = clientId;
  if (status !== undefined) params.status = status;
  if (search) params.search = search;
  
  return api.get<JobsResponse>(url.GET_JOBS, params);
};

/**
 * Get job by ID
 * Uses /api/v1/Job endpoint (formerly Project)
 * @param id - Job ID (UUID)
 * @returns Promise with job item (includes workOrders array)
 */
export const getProjectById = (id: string): Promise<JobItem> => {
  return api.get<JobItem>(`${url.GET_JOB_BY_ID}/${id}`);
};

/**
 * Create a new job
 * Uses /api/v1/Job endpoint (formerly Project)
 * @param payload - Job create request payload
 * @returns Promise with created job item
 */
export const createProject = (payload: JobCreateRequest): Promise<JobItem> => {
  return api.create<JobItem>(url.CREATE_JOB, payload);
};

/**
 * Update an existing job (Title/Description/PrimarySiteId only)
 * Uses /api/v1/Job endpoint (formerly Project)
 * @param id - Job ID (UUID)
 * @param payload - Job update request payload
 * @returns Promise with updated job item
 */
export const updateProject = (
  id: string,
  payload: JobUpdateRequest
): Promise<JobItem> => {
  return api.put<JobItem>(`${url.UPDATE_JOB}/${id}`, payload);
};

/**
 * Get jobs by client ID (paginated)
 * Uses /api/v1/Job/by-client endpoint (formerly Project)
 * @param clientId - Client ID (UUID)
 * @param pageNumber - Page number (default: 1)
 * @param pageSize - Page size (default: DEFAULT_PAGE_SIZE)
 * @returns Promise with paginated jobs response
 */
export const getProjectsByClient = (
  clientId: string,
  pageNumber: number = 1,
  pageSize: number = DEFAULT_PAGE_SIZE
): Promise<JobsResponse> => {
  return api.get<JobsResponse>(`${url.GET_JOBS_BY_CLIENT}/${clientId}`, {
    pageNumber,
    pageSize,
  });
};

/**
 * Get job by quote ID (1:1 relationship)
 * Uses /api/v1/Job/by-quote endpoint (formerly Project)
 * @param quoteId - Quote ID (UUID)
 * @returns Promise with job item
 */
export const getProjectByQuoteId = (quoteId: string): Promise<JobItem> => {
  return api.get<JobItem>(`${url.GET_JOB_BY_QUOTE_ID}/${quoteId}`);
};
